import { type DragEvent, useRef, useState } from 'react';

export const useReorderDrag = ({
  scope,
  ids,
  disabled,
  onMove,
  axis = 'y',
}: {
  scope: string;
  ids: readonly string[];
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  axis?: 'x' | 'y';
}) => {
  const source = useRef<string | null>(null);
  const [target, setTarget] = useState<{ id: string; after: boolean } | null>(null);
  const format = `application/x-portfolio-order-${scope}`;
  const reset = () => {
    source.current = null;
    setTarget(null);
  };
  const position = (event: DragEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return axis === 'x'
      ? event.clientX > rect.left + rect.width / 2
      : event.clientY > rect.top + rect.height / 2;
  };
  return {
    target,
    handleProps: (id: string) => ({
      draggable: !disabled && ids.includes(id),
      onDragStart: (event: DragEvent<HTMLButtonElement>) => {
        if (disabled || !ids.includes(id)) {
          event.preventDefault();
          return;
        }
        source.current = id;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData(format, id);
        event.dataTransfer.setData('text/plain', id);
      },
      onDragEnd: reset,
    }),
    rowProps: (id: string) => ({
      onDragOver: (event: DragEvent<HTMLElement>) => {
        if (disabled || !source.current || !ids.includes(source.current) || !ids.includes(id))
          return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setTarget({ id, after: position(event) });
      },
      onDrop: (event: DragEvent<HTMLElement>) => {
        const from = source.current ? ids.indexOf(source.current) : -1;
        const at = ids.indexOf(id);
        if (
          !disabled &&
          from >= 0 &&
          at >= 0 &&
          event.dataTransfer.getData(format) === source.current
        ) {
          event.preventDefault();
          const insertion = at + (position(event) ? 1 : 0);
          const to = insertion - (from < insertion ? 1 : 0);
          if (from !== to) onMove(from, to);
        }
        reset();
      },
    }),
  };
};
